/**
 * SuperAdminLayout - Layout wrapper for super admin pages
 *
 * Features:
 * - Tabbed navigation for Airlines, Fleets, and Doc Types
 * - Only accessible to super admins (Global airline admin)
 * - Consistent header and navigation
 * - Highlights active tab
 *
 * Usage:
 * <SuperAdminLayout>
 *   <AirlinesAdmin />
 * </SuperAdminLayout>
 */

import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAirline } from '../../contexts/AirlineContext';
import {
  Building,
  Truck,
  FileText,
  ArrowLeft,
  Settings,
  Folder,
  BarChart3,
  Sliders,
  DollarSign,
  MonitorCog,
} from 'lucide-react';

// =====================================================================
// Types
// =====================================================================

interface SuperAdminLayoutProps {
  children: React.ReactNode;
}

interface NavTab {
  name: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

// =====================================================================
// Navigation Tabs
// =====================================================================

const NAV_TABS: NavTab[] = [
  {
    name: 'Airlines',
    path: '/admin/airlines',
    icon: Building,
    description: 'Manage airlines and configurations',
  },
  {
    name: 'Fleet Models',
    path: '/admin/fleets',
    icon: Truck,
    description: 'Manage aircraft fleet models',
  },
  {
    name: 'Document Types',
    path: '/admin/doc-types',
    icon: FileText,
    description: 'Manage document types and retrieval parameters',
  },
  {
    name: 'Airline Overrides',
    path: '/admin/airline-overrides',
    icon: Settings,
    description: 'Manage airline-specific doc type parameter overrides',
  },
  {
    name: 'Documents',
    path: '/admin/documents',
    icon: Folder,
    description: 'Manage document library and properties',
  },
  {
    name: 'Retrieval Policies',
    path: '/admin/retrieval-policies',
    icon: Sliders,
    description: 'Configure retrieval parameters per airline/fleet/stage',
  },
  {
    name: 'Retrieval Diagnostics',
    path: '/admin/retrieval-diagnostics',
    icon: BarChart3,
    description: 'View retrieval_log metrics by stage and fleet',
  },
  {
    name: 'API Costs',
    path: '/admin/api-costs',
    icon: DollarSign,
    description: 'Monitor API usage costs for OpenAI and AssemblyAI',
  },
  {
    name: 'System Ops',
    path: '/admin/system-ops',
    icon: MonitorCog,
    description: 'System configuration and pipeline monitoring',
  },
];

// =====================================================================
// Component
// =====================================================================

export const SuperAdminLayout: React.FC<SuperAdminLayoutProps> = ({ children }) => {
  const { isSuperAdmin } = useAirline();
  const navigate = useNavigate();

  // ===================================================================
  // Permission Check
  // ===================================================================

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">
            You need super admin permissions to access this section.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-primary flex items-center gap-2 mx-auto"
          >
            <ArrowLeft size={20} className="w-5 h-5" />
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ===================================================================
  // Render
  // ===================================================================

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Super Admin Panel</h1>
              <p className="text-sm text-gray-600">
                Global configuration and management
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <ArrowLeft size={16} className="w-4 h-4" />
              Back to Dashboard
            </button>
          </div>

          {/* Tabbed Navigation */}
          <nav className="flex space-x-1" aria-label="Tabs">
            {NAV_TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <NavLink
                  key={tab.path}
                  to={tab.path}
                  className={({ isActive }) =>
                    `group flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'bg-sky-50 text-sky-700 border border-sky-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border border-transparent'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon
                        className={`w-5 h-5 ${
                          isActive ? 'text-sky-600' : 'text-gray-400 group-hover:text-gray-600'
                        }`}
                      />
                      <span>{tab.name}</span>
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="w-full">
        {children}
      </main>
    </div>
  );
};

// =====================================================================
// Export with Default
// =====================================================================

export default SuperAdminLayout;
